import { computed, ref } from 'vue'
import { api, type BusinessPartner, type PaginatedResponseDtoOfBusinessPartner } from '@/api/eOrdersApi'
import { useApiAdapter } from '@/store'
import { deserializeJson } from '@/utils/jsonDeserializer'

type LoadingStatus = 'idle' | 'pending' | 'success' | 'error'

export interface BusinessPartnerFilters {
  search?: string
}

export interface BusinessPartnerSource {
  system: string
  id: string
  name: string
}

export interface TransformedBusinessPartner {
  id: string
  displayName: string
  partnerType: string
  sources: BusinessPartnerSource[]
}

const toArray = <T>(value: any): T[] => {
  if (!value) return []
  
  if (Array.isArray(value)) return value
  
  if (value.$values && Array.isArray(value.$values)) {
    return value.$values
  }
  
  return []
}

const transformBusinessPartner = (partner: BusinessPartner): TransformedBusinessPartner => {
  const candidateSources = [
    { key: 'businessPartner', fallback: 'Core' },
    { key: 'sapBusinessPartner', fallback: 'SAP' },
    { key: 'nolsBusinessPartner', fallback: 'NOLS' }
  ] as const

  const sources = candidateSources
    .map(({ key, fallback }) => {
      const source = partner[key]
      if (!source) return null

      return {
        system: source.$sourceSystem ?? fallback,
        id: source.id ?? '',
        name: source.name ?? ''
      }
    })
    .filter(Boolean) as BusinessPartnerSource[]
  
  const displayName = partner.name ?? sources[0]?.name ?? ''
  const identifier = partner.id ?? sources[0]?.id ?? ''
  const partnerType = partner.partnerType ?? partner.type ?? partner.category ?? ''

  return {
    id: identifier,
    displayName,
    partnerType,
    sources
  }
}

const fallbackEmptyResponse: PaginatedResponseDtoOfBusinessPartner = {
  pageSize: 0,
  currentPage: 0,
  totalItems: 0,
  totalPages: 0,
  data: []
}

export const useBusinessPartnersData = () => {
  const apiAdapter = useApiAdapter()

  const rawData = ref<PaginatedResponseDtoOfBusinessPartner | null>(null)
  const status = ref<LoadingStatus>('idle')
  const error = ref<string | null>(null)
  const currentPage = ref(1)
  const pageSize = ref(20)
  const currentFilters = ref<BusinessPartnerFilters>({})

  const data = computed<TransformedBusinessPartner[]>(() => {
    if (!rawData.value) {
      return []
    }

    const dataset = rawData.value.data
    const collection = toArray<BusinessPartner>(dataset)

    return collection.map(transformBusinessPartner)
  })

  const pagination = computed(() => {
    if (!rawData.value) return null

    return {
      pageSize: rawData.value.pageSize ?? pageSize.value,
      currentPage: rawData.value.currentPage ?? currentPage.value,
      totalItems: rawData.value.totalItems ?? 0,
      totalPages: rawData.value.totalPages ?? 0
    }
  })

  const fetchBusinessPartners = async (size: number = pageSize.value, page: number = currentPage.value, filters: BusinessPartnerFilters = currentFilters.value, suppressStatusChange = false) => {
    try {
      if (!suppressStatusChange) {
        status.value = 'pending'
      }
      error.value = null

      const searchTerm = filters.search?.trim()
      const response = await apiAdapter.invoke(config =>
        api.getBusinessPartners(page, size, searchTerm || undefined, config)
      )

      if (!response) {
        throw new Error('Failed to load business partners.')
      }

      const deserialized = deserializeJson(response) as PaginatedResponseDtoOfBusinessPartner

      rawData.value = deserialized
      status.value = 'success'
      currentPage.value = page
      pageSize.value = size
      currentFilters.value = filters
    } catch (err) {
      console.error('Failed to fetch business partners', err)
      status.value = 'error'
      error.value = err instanceof Error ? err.message : 'Unknown error'
      rawData.value = {
        ...fallbackEmptyResponse,
        pageSize: size,
        currentPage: page
      }
    }
  }

  const applyFilters = async (filters: BusinessPartnerFilters) => {
    currentFilters.value = filters
    currentPage.value = 1
    
    await fetchBusinessPartners(pageSize.value, 1, filters, true)
  }

  const clearFilters = async () => {
    currentFilters.value = {}
    currentPage.value = 1
    
    await fetchBusinessPartners(pageSize.value, 1, {}, true)
  }

  const goToPage = async (page: number) => {
    if (page <= 0 || page === currentPage.value) return
    
    await fetchBusinessPartners(pageSize.value, page, currentFilters.value)
  }

  const changePageSize = async (newSize: number) => {
    if (newSize === pageSize.value) return
    
    await fetchBusinessPartners(newSize, 1, currentFilters.value)
  }

  return {
    data,
    status,
    error,
    pagination,
    currentPage: computed(() => currentPage.value),
    pageSize: computed(() => pageSize.value),
    currentFilters: computed(() => currentFilters.value),
    fetchBusinessPartners,
    applyFilters,
    clearFilters,
    goToPage,
    changePageSize
  }
}

